const TOOLTIP_CLASS = 'power-chart-tooltip';
const ROW_CLASS = 'power-chart-tooltip__row';

function formatMetricValue(value, decimals = 1) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function appendTooltipHeader(tooltipEl, label) {
  const header = document.createElement('div');
  header.className = 'power-chart-tooltip__header';
  header.textContent = label;
  tooltipEl.appendChild(header);
}

function appendTooltipRow(tooltipEl, label, value) {
  const row = document.createElement('div');
  row.className = ROW_CLASS;

  const labelEl = document.createElement('span');
  labelEl.textContent = label;

  const valueEl = document.createElement('span');
  valueEl.textContent = value;

  row.appendChild(labelEl);
  row.appendChild(valueEl);
  tooltipEl.appendChild(row);
}

function getOrCreateTooltip() {
  let tooltipEl = document.body.querySelector(`.${TOOLTIP_CLASS}`);
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = TOOLTIP_CLASS;
    tooltipEl.setAttribute('role', 'tooltip');
    document.body.appendChild(tooltipEl);
  }

  return tooltipEl;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function positionTooltip(chart, tooltip, tooltipEl) {
  const canvasRect = chart.canvas.getBoundingClientRect();
  const caretX = canvasRect.left + tooltip.caretX;
  const caretY = canvasRect.top + tooltip.caretY;
  const tooltipWidth = tooltipEl.offsetWidth;
  const tooltipHeight = tooltipEl.offsetHeight;
  const gap = 8;
  const edge = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = caretX - tooltipWidth / 2;
  left = clamp(left, edge, Math.max(edge, viewportWidth - tooltipWidth - edge));

  let top = caretY - gap - tooltipHeight;
  if (top < edge) {
    top = caretY + gap;
  }
  top = clamp(top, edge, Math.max(edge, viewportHeight - tooltipHeight - edge));

  tooltipEl.style.opacity = '1';
  tooltipEl.style.position = 'fixed';
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.transform = 'none';
}

function hideTooltip(tooltipEl) {
  if (tooltipEl) {
    tooltipEl.style.opacity = '0';
  }
}

export function hidePowerChartTooltip() {
  hideTooltip(document.body.querySelector(`.${TOOLTIP_CLASS}`));
}

/** Drop the power-curve tooltip when the finger leaves the screen. */
export function releasePowerChartTouchTooltip(chart) {
  hidePowerChartTooltip();
  if (!chart || chart.destroyed) {
    return;
  }

  chart.setActiveElements([]);
  if (chart.tooltip) {
    chart.tooltip.setActiveElements([], { x: 0, y: 0 });
    chart.tooltip.opacity = 0;
  }
  chart.update('none');
}

export function createPowerChartTouchReleasePlugin() {
  return {
    id: 'powerChartTouchRelease',
    afterInit(chart) {
      const release = () => releasePowerChartTouchTooltip(chart);
      chart.canvas.addEventListener('touchend', release);
      chart.canvas.addEventListener('touchcancel', release);
      chart.$releasePowerChartTooltip = release;
    },
    beforeDestroy(chart) {
      const release = chart.$releasePowerChartTooltip;
      if (release && chart.canvas) {
        chart.canvas.removeEventListener('touchend', release);
        chart.canvas.removeEventListener('touchcancel', release);
      }
      delete chart.$releasePowerChartTooltip;
    },
  };
}

export function createPowerChartHoverCrosshairPlugin() {
  return {
    id: 'powerChartHoverCrosshair',
    afterDatasetsDraw(chart) {
      const { tooltip, chartArea, ctx } = chart;
      if (!tooltip || tooltip.opacity === 0 || !chartArea) {
        return;
      }

      const x = tooltip.caretX;
      if (typeof x !== 'number' || !Number.isFinite(x)) {
        return;
      }

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(249, 76, 175, 0.72)';
      ctx.shadowColor = 'rgba(249, 76, 175, 0.35)';
      ctx.shadowBlur = 6;
      ctx.stroke();
      ctx.restore();
    },
  };
}

export function createPowerChartExternalTooltip(getPointData, t) {
  return function externalTooltip(context) {
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip();

    if (!tooltipEl) {
      return;
    }

    if (!chart?.canvas?.isConnected || tooltip.opacity === 0) {
      hideTooltip(tooltipEl);
      return;
    }

    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
    const pointData = getPointData(dataIndex, tooltip.dataPoints, chart);

    if (!pointData) {
      hideTooltip(tooltipEl);
      return;
    }

    tooltipEl.replaceChildren();

    if (pointData.timeLabel) {
      appendTooltipHeader(tooltipEl, pointData.timeLabel);
    }

    const powerValue = formatMetricValue(pointData.power, 1);
    if (powerValue != null) {
      appendTooltipRow(tooltipEl, t('historyPage.power'), `${powerValue} ${t('kW')}`);
    }

    const voltageValue = formatMetricValue(pointData.voltage, 0);
    if (voltageValue != null) {
      appendTooltipRow(tooltipEl, t('historyPage.voltage'), `${voltageValue} ${t('v')}`);
    }

    const socValue = formatMetricValue(pointData.soc, 0);
    if (socValue != null && pointData.soc > 0) {
      appendTooltipRow(tooltipEl, t('historyPage.soc'), `${socValue}%`);
    }

    positionTooltip(chart, tooltip, tooltipEl);
  };
}
